import introScreen from "./intro_screen.json";
import introTapToStart from "./intro_tap_to_start.json";
import lines from "./lines.json";
import miniPaytable from "./mini_paytable.json";
import winText from "./win_text.json";
import winCelebration from "./win_celebration.json";
import winCounter from "./win_counter.json";
import winCelebrationCounter from "./win_celebration_counter.json";
import winCelebrationXBet from "./win_celebration_xbet.json";
import jackpots from "./jackpots.json";
// The coin number takes the JACKPOT COIN's lettering (cj's baked `coin_major`
// art, measured from its atlas pixels 2026-09-29) in black and white, in Noto
// Sans ExtraBold (the closest shipped face — not Troika): a near-
// white top fading through grey to dark grey, a dark outline, a solid lip
// straight down. Each colour is the jackpot word's, by luminance. Vladimir:
// "this same gradient only black and white".
// import coinValue from "./coin_value.json";
import coinValue from "./coin_value_bw.json";
import goldenCoin from "./golden_coin.json";
// A JACKPOT coin's number, once a Scarab has multiplied it and its tier word
// has given way to the value (cj `double_value`) — the tier word's own colours,
// sampled top-to-bottom off cj's baked lettering (coin_mini … coin_grand,
// 2026-09-29), on coin_value_bw's shape: same face, weight, outline, lip.
import coinValueMini from "./coin_value_mini.json";
import coinValueMinor from "./coin_value_minor.json";
import coinValueMidi from "./coin_value_midi.json";
import coinValueMajor from "./coin_value_major.json";
import coinValueGrand from "./coin_value_grand.json";
import gambleLevel from "./gamble_level.json";
import gambleButton from "./gamble_button.json";
import gambleFreeSpins from "./gamble_freespins.json";
import tapToContinue from "./tap_to_continue.json";
import freeSpinsCongrats from "./free_spins_congrats.json";
import freeSpinsText from "./free_spins_text.json";
import zeroWinText from "./zero_win_text.json";
// ── The animator's text styles (2026-09-30) — they REPLACE ours ──
// coin, jackpot values: Kanit-Medium, white -> pale blue, dark purple outline
// dialogs:              NotoSans-Black, cream -> yellow, dark red outline
// jackpot win numbers:  the same as dialogs, with a drop shadow
// Applied at the bottom of this file (after scaleStyle), each at the size the
// style it replaces was drawn at.
import coinJackpotValues from "./coin_jackpot_values.json";
import dialogs from "./dialogs.json";
// import jackpotWinNumbers from "./jackpot_win_numbers.json"; // its shadow is not in the design — dialogs look used instead


export const FONT_STYLES = {
  introScreen,
  introTapToStart,
  lines,
  miniPaytable,
  winText,
  winCelebration,
  winCounter,
  winCelebrationCounter,
  winCelebrationXBet,
  jackpots,
  coinValue,
  // The jackpot coin's revealed number, one per tier (see the imports).
  coinValueMini,
  coinValueMinor,
  coinValueMidi,
  coinValueMajor,
  coinValueGrand,
  // One style per coin skin — the four value tiers of the Golden Mystery's
  // Instant Prize. Supplied by the animator, kept verbatim except for the
  // fallback chain on fontFamily, so the colours and the stroke stay theirs.
  goldenCoin,
  gambleLevel,
  gambleButton,
  // Supplied as "NotoSans-Black", which is not a family this game registers.
  // injectStyles declares "Noto Sans" at weights 300-900, so Black is that
  // family at weight 900.
  gambleFreeSpins,
  // The tap-to-continue word on every Mad Labs popup. Zeus used
  // introTapToStart; attachTapToContinue defaults to this one.
  tapToContinue,
  // Free-spin intro popup: the cyan CONGRATULATIONS and the yellow labels.
  freeSpinsCongrats,
  freeSpinsText,
  // BETTER LUCK NEXT TIME. win_text (the intro popup's MAX WIN) in red and
  // larger — everything else about it is unchanged.
  zeroWinText,
};

/**
 * How much bigger the WIN CELEBRATION texts are than their authored sizes.
 *
 * ONE number for four screens. The base game's win popup and the three
 * free-spin popups — Total Win, Max Win and Zero Win — all draw the SAME
 * `win_celebration` spine, so a size set in one of them and not the others
 * shows up as the same picture at two sizes depending on which one opened.
 * That is exactly what happened when the base game's texts were raised alone.
 *
 * It multiplies the AUTHORED sizes, which stay the artist's:
 *
 *   winCelebrationCounter  90  ->  104
 *   winCelebrationXBet     48  ->   55
 *
 * The 90:48 relationship is deliberate — the counter is the win, the xbet is
 * the footnote under it. Scaling both by one factor keeps that difference;
 * giving either its own number is how they drift into being the same size.
 *
 * Applied through scaleStyle, so each style's stroke, shadow distance and blur
 * scale with it rather than staying at the size they were drawn for.
 */
export const WIN_TEXT_SCALE = 1.15;

/**
 * Scale a font style to a new fontSize while keeping pixel-based properties proportional.
 * Takes the JSON style as the "reference" design and scales dropShadowDistance and
 * strokeThickness proportionally to the ratio (newFontSize / originalFontSize).
 *
 * Usage:
 *   const style = scaleStyle(FONT_STYLES.introScreen, 24);
 *   const pixiStyle = new PIXI.TextStyle(style);
 *
 * If no fontSize override is provided, returns the original style unchanged.
 *
 * @param {Object} baseStyle - The reference font style from JSON
 * @param {number} [fontSizeOverride] - Desired fontSize (omit to use original)
 * @param {Object} [extraProps] - Additional PIXI.TextStyle properties to merge (e.g., wordWrap)
 * @returns {Object} New style object with proportionally scaled properties
 */
export const scaleStyle = (baseStyle, fontSizeOverride, extraProps) => {
  const originalFontSize = baseStyle.fontSize;
  const fontSize = fontSizeOverride || originalFontSize;
  const ratio = fontSize / originalFontSize;

  return {
    ...baseStyle,
    fontSize,
    // Scale pixel-based properties proportionally
    ...(baseStyle.dropShadowDistance != null && {
      dropShadowDistance: Math.round(baseStyle.dropShadowDistance * ratio),
    }),
    ...(baseStyle.strokeThickness != null && {
      strokeThickness: Math.round(baseStyle.strokeThickness * ratio) || 1,
    }),
    ...(baseStyle.dropShadowBlur != null && {
      dropShadowBlur: Math.round(baseStyle.dropShadowBlur * ratio),
    }),
    // Letter spacing is pixels too (the coin look's, 2026-09-30).
    ...(baseStyle.letterSpacing != null && {
      letterSpacing: baseStyle.letterSpacing * ratio,
    }),
    ...extraProps,
  };
};

// ============================================================================
// THE ANIMATOR'S STYLES (Vladimir, 2026-09-30: "we remove our own style and
// apply that") — Troika is gone from the game, so every style that drew in it
// takes one of these. Each keeps the fontSize of the style it replaces (its
// callers size from it), with stroke / shadow scaled by scaleStyle.
//
// Two keys of the coin style are NOT taken: `padding: 690` (the export tool's
// canvas margin — it would grow every coin texture by 690 px a side) and
// `lineHeight: 100` (a 150 px font's leading; single-line numbers need none).
// ============================================================================
// const { padding: _padding, lineHeight: _lineHeight, ...coinLook } = coinJackpotValues;
const { padding: _padding, lineHeight: _lineHeight, ...coinDelivered } = coinJackpotValues;
// Tuned to the DESIGN picture (Vladimir, 2026-09-30: outline thicker, the
// digits further apart, and the outline "really pixelated"):
//   strokeThickness 14 -> 20   PIXI draws the stroke half under the fill, so
//                              the visible edge was only 7 of 150
//   letterSpacing 8            the design's digit spacing (scaled with size)
//   lineJoin "round"           the default "miter" cut a spike at every corner
//                              of the outline — the jagged, pixelated edge
const coinLook = {
  ...coinDelivered,
  strokeThickness: 20,
  // letterSpacing: 8,
  // letterSpacing: 12, // "bump it up a little bit" (Vladimir, 2026-09-30)
  // With evenGlyphGaps it is the gap between the characters' DRAWN edges
  // (fill), so it must clear the outline: 20 of stroke (10 outside each
  // glyph) + a visible gap.
  // letterSpacing: 26,
  // letterSpacing: 23, // "shrink it a little bit" (Vladimir, 2026-09-30)
  letterSpacing: 20, // "go to 20" (Vladimir, 2026-09-30)
  lineJoin: "round",
  // // Every digit the same gap: no kerning pairs (".2" was tucked in).
  // fontKerning: "none",
  // Every character the same gap from the next, whatever its shape — read by
  // textLabelCache (drawEvenGaps), not a PIXI.TextStyle key.
  evenGlyphGaps: true,
  // Headroom for the centred row (see drawEvenGaps).
  padding: 8,
};
// The dialogs / jackpot numbers look, tuned to the DESIGN lettering
// (Vladimir's "HOLD & WIN" reference, 2026-09-30): the delivered 10 of stroke
// shows only 5 (PIXI draws half of it under the fill) — the design's outline
// is about 11 of 150 visible, so 22; and round corners on it, as there.
const dialogsLook = { ...dialogs, strokeThickness: 22, lineJoin: "round" };
// const jackpotWinNumbersLook = {
//   ...jackpotWinNumbers,
//   strokeThickness: 22,
//   lineJoin: "round",
// };
// The dialogs' numbers take the dialogs look too — the design has NO drop
// shadow under them either (Vladimir, 2026-09-30): jackpots, dialog words and
// dialog numbers are one style.
const jackpotWinNumbersLook = dialogsLook;
const restyle = (look, old) => scaleStyle(look, old?.fontSize ?? look.fontSize);
// A jackpot coin's revealed number: the coin look in its tier's colours
// (the tier word's fill and outline — see coin_value_<tier>.json).
const tierLook = (tier) => ({
  ...restyle(coinLook, FONT_STYLES.coinValue),
  fill: tier.fill,
  fillGradientStops: tier.fillGradientStops,
  stroke: tier.stroke,
});

Object.assign(FONT_STYLES, {
  // The delivered looks themselves, for code that sizes them on its own.
  coinJackpotValues: coinLook,
  dialogs: dialogsLook,
  jackpotWinNumbers: jackpotWinNumbersLook,
  // Coins — reels, Hold & Win cells, chests, the collector's total.
  coinValue: restyle(coinLook, FONT_STYLES.coinValue),
  coinValueMini: tierLook(FONT_STYLES.coinValueMini),
  coinValueMinor: tierLook(FONT_STYLES.coinValueMinor),
  coinValueMidi: tierLook(FONT_STYLES.coinValueMidi),
  coinValueMajor: tierLook(FONT_STYLES.coinValueMajor),
  coinValueGrand: tierLook(FONT_STYLES.coinValueGrand),
  // The jackpot plates' values (GameViewSimple / GameView).
  // jackpots: restyle(coinLook, FONT_STYLES.jackpots),
  // The GOLDEN look, not the coin's — the design's plates are cream -> yellow
  // with the dark red outline (Vladimir, 2026-09-30).
  // jackpots: restyle(jackpotWinNumbersLook, FONT_STYLES.jackpots),
  // Outline only, no drop shadow — `dialogs` (Vladimir, 2026-09-30).
  jackpots: restyle(dialogsLook, FONT_STYLES.jackpots),
  // The words in the popups.
  freeSpinsCongrats: restyle(dialogsLook, FONT_STYLES.freeSpinsCongrats),
  freeSpinsText: restyle(dialogsLook, FONT_STYLES.freeSpinsText),
  zeroWinText: restyle(dialogsLook, FONT_STYLES.zeroWinText),
  gambleButton: restyle(dialogsLook, FONT_STYLES.gambleButton),
  // The numbers in the popups (and the line-win amounts).
  winCelebrationCounter: restyle(jackpotWinNumbersLook, FONT_STYLES.winCelebrationCounter),
  winCelebrationXBet: restyle(jackpotWinNumbersLook, FONT_STYLES.winCelebrationXBet),
  winText: restyle(jackpotWinNumbersLook, FONT_STYLES.winText),
  gambleLevel: Object.fromEntries(
    Object.entries(FONT_STYLES.gambleLevel || {}).map(([level, style]) => [
      level,
      restyle(jackpotWinNumbersLook, style),
    ]),
  ),
});
